/**
 * The sellable products this app hosts. "Product" here means the package a
 * customer buys (spec §1.1 point 5); the industry vocabulary inside
 * skyPerformance is a "vertical pack" and lives elsewhere.
 *
 * `skyhire` is first and is the default everywhere a product is optional, so
 * code written before products existed keeps meaning hire.
 *
 * `skyperformance_it` is "skyPerformance IT" (spec 2026-10-26), a separate product for IT/corporate
 * reviews. It is last so every PRODUCT_KEYS-ordered list keeps hire, then Performance, as before.
 */
export const PRODUCT_KEYS = ["skyhire", "skyperformance", "skyperformance_it"] as const;
export type ProductKey = (typeof PRODUCT_KEYS)[number];
export const DEFAULT_PRODUCT: ProductKey = "skyhire";

export function isProductKey(value: unknown): value is ProductKey {
  return typeof value === "string" && (PRODUCT_KEYS as readonly string[]).includes(value);
}

/**
 * The products a request may name: staff enabling (instance products PUT and its panel),
 * provisioning, role assignment and the per-product permission read. A new product key joins this
 * list only together with the server side that handles it, so every one of those paths flips here,
 * in one place; until then it is refused exactly as an unknown key. Today: every product.
 */
export const ENABLEABLE_PRODUCT_KEYS = [
  "skyhire",
  "skyperformance",
  "skyperformance_it",
] as const satisfies readonly ProductKey[];
export type EnableableProductKey = (typeof ENABLEABLE_PRODUCT_KEYS)[number];

export function isEnableableProductKey(value: unknown): value is EnableableProductKey {
  return (
    typeof value === "string" && (ENABLEABLE_PRODUCT_KEYS as readonly string[]).includes(value)
  );
}

/** Display names for the switcher, toasts and the no-access screen (D14: "Hire / Performance"). */
export const PRODUCT_LABELS: Record<ProductKey, string> = {
  skyhire: "Hire",
  skyperformance: "Performance",
  skyperformance_it: "Performance IT",
};

/** Where each product's landing lives. The active product is derived from the URL (spec §2). */
export const PRODUCT_LANDING_PATH: Record<ProductKey, string> = {
  skyhire: "/",
  skyperformance: "/performance",
  skyperformance_it: "/it-performance",
};

/** API prefixes under /api/v1 that belong to one non-default product; hire has no single prefix. */
export const PRODUCT_API_PREFIX: Partial<Record<ProductKey, string>> = {
  skyperformance: "/performance",
  skyperformance_it: "/performance-it",
};

/** Wordmark assets, the one place their paths live (D14). `light` sits on dark chrome, `dark` on a light page. */
export const PRODUCT_WORDMARK: Record<ProductKey, { light: string; dark: string; alt: string }> = {
  skyhire: {
    light: "/dist/img/skyHire_Light.svg",
    dark: "/dist/img/skyHire_Dark.svg",
    alt: "skyHire",
  },
  skyperformance: {
    light: "/dist/img/skyPerformance_Light.svg",
    dark: "/dist/img/skyPerformance_Dark.svg",
    // The product owner's wordmark (2026-10-08) reads "Performance Management".
    alt: "Performance Management",
  },
  skyperformance_it: {
    // skyPerformance's artwork with an "IT" mark in its gold (brief C, 2026-10-26); replace the
    // files when design delivers final IT artwork.
    light: "/dist/img/skyPerformanceIT_Light.svg",
    dark: "/dist/img/skyPerformanceIT_Dark.svg",
    alt: "skyPerformance IT",
  },
};
